import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { periodWindow, type TargetWindow } from '@/lib/targets';
import type {
  TargetActual,
  TargetActualsCurrency,
  TargetActualsMyRegion,
  TargetActualsParams,
  TargetActualsRegion,
  TargetActualsScope,
  TargetActualsSubject,
  TargetBillingRegion,
  TargetFxMode,
  TargetMetric,
  TargetPeriodRecord,
  TargetTranslation,
} from '@/lib/types';

/**
 * Whose figures `useMyTargets` reads: the signed-in person's, every company in
 * the billing regions that name them as default account lead (`region`, new
 * 2026-10-23, sent as `scope: 'my_region'`), or the whole business's.
 */
export type TargetSubjectScope = 'mine' | 'region' | 'overall';

/**
 * The signed-in person's targets AND actuals over one window.
 *
 * `/targets/actuals` answers both at once: every metric with what was targeted
 * and what actually happened, including metrics carrying no target.
 */

/*
 * ⛔ `TILE_METRIC` WAS DELETED HERE ON 2026-09-11, AND IT IS NOT COMING BACK.
 *
 * It mapped a funnel tile to the metric it is measured by, and its `invoiced`
 * entry pointed at `booked` — under a comment explaining that the slug measures
 * ACCEPTED QUOTES while the label read "Invoiced revenue". That is exactly the
 * mislabelling the owner reported the same day (*"why is invoiced as 0"*), and
 * it is now fixed: `invoiced` is a real server metric measuring invoices raised,
 * and `booked` reads "Accepted revenue".
 *
 * 🔴 **IT HAD NO IMPORTERS, AND THAT IS WHY IT HAD TO GO RATHER THAN BE
 *    RE-POINTED.** `TargetTiles.tsx` stopped reading it and now carries the
 *    mapping on each tile itself. An unreferenced export is not inert — this
 *    repo's own record says a dead map "is a wrong answer waiting for somebody
 *    to look something up", and it is the version of the documentation that
 *    nothing tests. Left in place it would have gone on asserting
 *    `invoiced -> booked` for the next reader who grepped for it.
 *
 * ⚠️ **Grepping the NAME still finds hits and every one is a comment like this.**
 *    Grep the SURFACE — an `import`, a call site — never the name, and run a
 *    control so an empty result is distinguishable from a misspelled query.
 */

export interface MyTargets {
  byMetric: Partial<Record<TargetMetric, TargetActual>>;
  /**
   * The catalogued period this dashboard defaults to, and **the thing the
   * quarter and month subdivisions are cut from**.
   *
   * ⚠️ Exposed rather than kept private because the picker cannot be built
   * without it: a quarter of this cycle is May–Jul only if the cycle starts in
   * May, and that fact lives on this row and nowhere else. `null` while
   * loading, on error, and when no period covers today.
   */
  period: TargetPeriodRecord | null;
  /**
   * 🔴 **EVERY CATALOGUED ACTIVE PERIOD — the list `TargetWindowPicker`'s CYCLE
   * control offers, new 2026-09-13.**
   *
   * ⚠️ **THE LIST WAS ALWAYS READ AND WAS NEVER EXPOSED, WHICH IS WHY STEP ONE OF
   * THAT PICKER WAS MISSING RATHER THAN BADLY BUILT.** The effect below has
   * fetched `?isActive=true&perPage=100` since this hook was written and then
   * collapsed it to ONE record through `periodForToday`, so a caller could see
   * the period it defaulted to and had no way to name another. The drilldown into
   * quarters and months was complete; there was simply nothing to drill into but
   * today's.
   *
   * ⛔ **DO NOT LET A CALLER FETCH THIS AGAIN.** One read per user is the whole
   * budget — see the effect's own note — and two copies of one list can disagree
   * about which periods exist while both look right.
   *
   * ⚠️ **IT IS NOT FILTERED BY `kind` HERE, deliberately.** The picker wants
   * `kind: 'cycle'` and filters for itself; a hook narrowing on a caller's behalf
   * would make the row it pins back — a `year` period covering today, which is an
   * ordinary default — unreachable from the only place that can see it is
   * missing.
   *
   * ⚠️ **`perPage: 100` is the server's ceiling and nothing reports a shortfall.**
   * One period exists today, so this is years of headroom rather than a live
   * hazard; if the catalogue ever passes 100, this needs paging the way
   * `listAllPermissions` pages, never a bigger number.
   *
   * Empty while loading, on error, and where nothing is catalogued.
   */
  periods: readonly TargetPeriodRecord[];
  /**
   * The window these figures describe.
   *
   * ⚠️ **This is what a header must be labelled from — never the picker's own
   * state, and never the period's name.** Where the server echoes the window it
   * measured, that echo is what lands here; where it does not, the window that
   * was requested does, which is exactly right because the request is the only
   * thing that decided the span.
   */
  window: TargetWindow | null;
  /**
   * 🔴 **THE CURRENCY THE SERVER ACTUALLY SUMMED IN. LABEL EVERY AMOUNT FROM
   * THIS, NEVER FROM THE PICKER'S STATE AND NEVER FROM A METRIC'S
   * `baseCurrency`** — that one is the stored TARGET's currency, a different
   * fact, `null` on a count and on any metric nobody set a target for.
   *
   * ⚠️ **`null` means the server did not say**, which is an API predating the
   * `baseCurrency` parameter — and it summed in GBP, because that was the only
   * thing it could do. A caller must render that case rather than assume its
   * request was honoured: asking for USD and being answered in GBP under a USD
   * label is a wrong number wearing a plausible unit, which is strictly worse
   * than the understatement the parameter exists to fix, because nothing about
   * it looks wrong.
   *
   * ⚠️ **`null` on every non-figures state too** — loading, failed, no period.
   * Nothing was measured, so nothing may claim a currency; echoing the REQUESTED
   * code there would put a unit on a figure that was never computed.
   */
  measuredInCurrency: string | null;
  /**
   * 🔴 **THE BILLING REGION THE SERVER SAYS IT NARROWED TO — read it, never the
   * argument that was passed in.** The two disagree exactly when it matters: an
   * API predating the parameter narrows nothing and echoes nothing, so a caller
   * assuming its request was honoured would head a whole-population figure with
   * one band's name.
   *
   * ⚠️ **`null` covers BOTH "no region was asked for" and "the server did not
   * say", and neither may be rendered as a band.** They agree about the figure —
   * it is the whole population — and differ only about what is known, so a
   * caller that has asked for a region and reads `null` back must say the
   * narrowing did not happen rather than print the region it wanted.
   *
   * ⚠️ **`null` on every non-figures state too** — loading, failed, no period.
   * Nothing was measured, so nothing may claim a scope.
   */
  measuredForBillingRegion: TargetBillingRegion | null;
  /**
   * The FX mode the SERVER echoed (owner 2026-10-28). Absent on the wire = an
   * older API = `'document'`; `'document'` on every non-figures state too.
   * Word the conversion note from this (`conversionNote`), never from the request.
   */
  fxMode: TargetFxMode;
  /**
   * The translation rate a `period_end` answer used; `null` in document mode,
   * from an older API, and on every non-figures state.
   */
  translation: TargetTranslation | null;
  /**
   * The exact `/targets/actuals` query these figures were measured with, or
   * `null` when nothing was measured successfully. The tile drill-down (owner
   * 2026-10-28) reuses it with `metric` added, so the rows can never be asked
   * for a different window, subject, currency or rates basis than the tile.
   */
  actualsQuery: TargetActualsParams | null;
  /**
   * The billing regions the server says a `region` read covered — the
   * response's `regions` echo. `undefined` on every other subject, on every
   * non-figures state, and from an older API that does not send it.
   */
  regions: readonly TargetActualsMyRegion[] | undefined;
  /**
   * The billing regions whose default account lead is this user, read ONCE per
   * user from `GET /billing-region-defaults` (four rows, open to every reader).
   * This is what decides whether "My region" is offered at all.
   *
   * `null` = not known yet, or the read failed, or an API predating
   * `accountLead` on that master; `[]` = this person leads no region.
   */
  ledRegions: readonly TargetActualsMyRegion[] | null;
  /**
   * Whose figures were actually asked for: the requested subject, except that
   * `region` reads as `mine` for somebody known to lead no region. Show THIS
   * on the toggle, never the remembered choice.
   */
  subject: TargetSubjectScope;
  loading: boolean;
  /**
   * ⚠️ A SEPARATE state from "no target set" and from "nothing has happened
   * yet". Three facts that look identical if collapsed, and only one is a
   * problem:
   *
   *   error           we could not find out
   *   target === null nobody committed to a number
   *   actual '0.00'   nothing closed in this window — a real, correct answer
   */
  error: string | null;
  /** No period covers today, so there is nothing to report against. */
  noPeriod: boolean;
}

const EMPTY: MyTargets = {
  byMetric: {},
  period: null,
  /*
   * ⚠️ **A FROZEN EMPTY ARRAY ON THE SHARED CONSTANT.** Every non-figures branch
   * spreads `EMPTY` and the settled ones override this key, so the literal is only
   * ever read where the list genuinely is unknown. It is not `readonly` at runtime
   * — the type is what forbids a caller mutating a constant every state shares.
   */
  periods: [],
  window: null,
  measuredInCurrency: null,
  measuredForBillingRegion: null,
  fxMode: 'document',
  translation: null,
  actualsQuery: null,
  regions: undefined,
  ledRegions: null,
  subject: 'mine',
  loading: false,
  error: null,
  noPeriod: false,
};

/** Bare `YYYY-MM-DD`, the API's own format — no `Date` arithmetic involved. */
function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * The period this dashboard DEFAULTS to.
 *
 * ⚠️ **Periods legitimately overlap** — a quarter sits inside a year and both
 * are real — which is exactly why the endpoint refuses to guess and requires a
 * window. Something still has to choose a default, so this takes the WIDEST
 * period covering today: an annual figure is what a dashboard headline answers,
 * and anything narrower is a narrowing somebody asks for.
 *
 * ⚠️ The choice is not hidden — the window is spelled out beside the figures,
 * and the picker beside it is how somebody asks for a narrower one.
 *
 * ⚠️ **IT IS A REDUCTION, AND SINCE 2026-09-13 THE LIST IT REDUCES IS EXPOSED
 * ALONGSIDE IT** — see `periods` on `MyTargets`. The list cannot be recovered from
 * this answer, which is the whole reason both are kept: this one says which cycle
 * a reader OPENS on, the list says which cycles they may choose instead.
 *
 * ⚠️ **`use-team-performance.ts` holds the twin of this function AND the twin of
 * that list.** **If either moves, move both** — and there are now THREE things
 * paired rather than one: this rule, the exposed list, and the `perPage` ceiling
 * behind it. Two dashboards disagreeing about which period is "now", or offering
 * different cycles, would put two different windows on screen under one word.
 */
function periodForToday(periods: readonly TargetPeriodRecord[]): TargetPeriodRecord | null {
  const today = todayIso();
  const covering = periods.filter((p) => p.startDate <= today && today <= p.endDate);
  if (covering.length === 0) return null;
  return covering.reduce((widest, p) =>
    p.startDate < widest.startDate ||
    (p.startDate === widest.startDate && p.endDate > widest.endDate)
      ? p
      : widest,
  );
}

/** Identity of a window as far as one request is concerned. */
function windowKey(window: TargetWindow | null): string {
  if (window === null) return '';
  return `${window.periodId ?? ''}|${window.from}|${window.to}`;
}

/**
 * ⚠️ **The window a request ASKED for is the fallback label, never the answer
 * where there is one.** The server echoes what it actually measured, and that
 * echo wins: a figure read under a label describing a different span is the one
 * failure this whole feature is arranged to prevent. An older server sends no
 * echo, and then the requested window is exactly right — the request is the
 * only thing that decided the span.
 *
 * A disagreement keeps the server's dates and DROPS the asked-for name, which
 * described a span the server did not measure. `Custom range · <dates>` is the
 * honest label for a window nothing here chose.
 */
function measuredWindow(asked: TargetWindow, echoed: { from?: string; to?: string }): TargetWindow {
  const { from, to } = echoed;
  if (from === undefined || to === undefined) return asked;
  if (from === asked.from && to === asked.to) return asked;
  return { name: 'Custom range', from, to, periodId: asked.periodId };
}

/**
 * What the caller is asking for.
 *
 * ⚠️ **THREE CASES, SPELLED OUT, BECAUSE TWO OF THEM WOULD OTHERWISE BOTH BE
 * `null`.** "report on whatever period covers today" and "report on nothing
 * yet" are opposite instructions one falsy value apart — the shape this schema
 * has already paid for with `ContactPersons: []` — so they are named rather
 * than encoded. `none` is what a half-typed or inverted date range resolves to:
 * a half-window is not a question this endpoint can be asked, and falling back
 * to the whole cycle there would put a year's money on screen under a picker
 * reading "Date range".
 */
export type TargetWindowChoice =
  | { kind: 'period' }
  | { kind: 'window'; window: TargetWindow }
  | { kind: 'none' };

export function useMyTargets(
  userId: number | null,
  choice: TargetWindowChoice,
  /**
   * 🔴 **WHICH CURRENCY TO SUM IN, AND IT IS PART OF THE CACHE KEY BELOW.**
   *
   * Omitted, the server sums in GBP — exactly what it did before the parameter
   * existed, so a caller that ignores this is byte-for-byte unchanged.
   *
   * ⚠️ **IT MUST JOIN `figuresKey`.** Without that, switching currency leaves
   * the cached figures on screen while the new code is printed beside them: a
   * USD dashboard showing GBP amounts labelled USD. A wrong number wearing a
   * plausible unit is worse than the understatement this parameter exists to
   * fix, because nothing about it looks wrong. `useTargetActuals` in
   * `use-team-performance.ts` joins its own key for the same reason.
   */
  baseCurrency?: string,
  /**
   * 🔴 **WHICH BILLING REGION TO NARROW TO, AND IT IS PART OF THE CACHE KEY
   * BELOW.**
   *
   * Omitted, the server sums the whole population — exactly what it did before
   * the parameter existed, so a caller that ignores this is byte-for-byte
   * unchanged. **The University Partnerships dashboard does ignore it**, by
   * decision: only the Management dashboard gets a region control.
   *
   * ⚠️ **IT MUST JOIN `figuresKey`, for the same reason `baseCurrency` does and
   * with a worse failure mode.** A region change leaves the WINDOW identical, so
   * keyed on the window alone the cached figures survive it and are relabelled
   * with the new band: the whole world's revenue under a heading reading Europe.
   * A currency mislabel at least prints a unit somebody might question; a region
   * mislabel prints a plausible figure with nothing beside it to doubt.
   *
   * ⚠️ **`'none'` is a REAL narrowing — the unbanded companies — and is not the
   * same as omitting the key.** `undefined` and `'none'` must therefore produce
   * different cache keys, which they do: the segment is empty for one and
   * `none` for the other.
   *
   * ⚠️ **`null` AND `undefined` BOTH MEAN "THE WHOLE POPULATION", and they are
   * folded at ONE boundary — the query builder below — rather than at every
   * call site.** A region control naturally holds `null` for its "All regions"
   * option while a caller with no control at all passes nothing; making the
   * hook refuse one of those spellings buys nothing and costs a `?? undefined`
   * at every mount, which is the sort of incantation that gets written as a
   * non-null assertion instead. **Neither reaches the wire** — both omit the
   * key — and both key as the empty segment, which is correct because they ask
   * the same question.
   *
   * ⛔ **Neither of them is `'none'`.** That is a third state and a real
   * narrowing; a `?? 'none'` here would turn "no scope chosen" into a claim
   * about the companies nobody has banded.
   */
  billingRegion?: TargetBillingRegion | null,
  /**
   * 🔴 **WHOSE FIGURES — the signed-in person's (`mine`, the default) or the
   * whole business's (`overall`), and it is part of the cache key below.**
   *
   * `overall` asks for `subjectType: 'organisation'`, which every reader who may
   * read targets at all may see — the server refuses only a `user` subject that
   * names somebody else — so no reader is turned away by choosing it.
   *
   * ⚠️ **It must join `figuresKey`.** Switching subject leaves the window, the
   * currency and the region identical, so without it the person's own figures
   * would survive the switch and be shown under "Overall".
   */
  requestedSubject: TargetSubjectScope = 'mine',
  /**
   * 🔴 **HOW FOREIGN AMOUNTS ARE CONVERTED (owner 2026-10-28), AND IT IS PART OF
   * THE CACHE KEY BELOW.** `'document'` (default) = each document at its own
   * date, today's behaviour, and the key is NOT sent; `'period_end'` = Xero's
   * P&L translation at one rate. Same failure as the currency if left out of
   * the key: one mode's figures under the other mode's note.
   */
  fxMode: TargetFxMode = 'document',
): MyTargets {
  /*
   * Both slices are stamped with what they describe, so `loading` is DERIVED at
   * render rather than written at the top of an effect — a synchronous setState
   * there starts a second render and trips `react(set-state-in-effect)`. The
   * stamps also stop one person's figures appearing under another's name, or
   * one window's figures under another's dates, for a frame after a change.
   */
  const [periodState, setPeriodState] = useState<{
    scopedTo: number;
    period: TargetPeriodRecord | null;
    /**
     * ⚠️ **THE WHOLE LIST IS KEPT BESIDE THE DEFAULT, not derived from it.**
     * `periodForToday` is a REDUCTION — the widest period covering today — so the
     * list cannot be recovered from its answer, and the cycle control needs the
     * list. Two slices of one response, stamped once, so they cannot disagree
     * about which read they came from.
     */
    periods: TargetPeriodRecord[];
    error: string | null;
  } | null>(null);

  const [figures, setFigures] = useState<{
    key: string;
    byMetric: Partial<Record<TargetMetric, TargetActual>>;
    window: TargetWindow;
    measuredInCurrency: string | null;
    measuredForBillingRegion: TargetBillingRegion | null;
    fxMode: TargetFxMode;
    translation: TargetTranslation | null;
    actualsQuery: TargetActualsParams | null;
    regions: TargetActualsMyRegion[] | undefined;
    error: string | null;
  } | null>(null);

  /*
   * Which billing regions name this user as their default account lead —
   * stamped with the user it describes, like the period slice. `regions: null`
   * is "unknown" (failed read, or an API that does not send `accountLead`).
   */
  const [ledState, setLedState] = useState<{
    scopedTo: number;
    regions: TargetActualsMyRegion[] | null;
  } | null>(null);

  useEffect(() => {
    if (userId === null) return;
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listBillingRegionDefaults(signal)
      .then((envelope) => {
        if (signal.aborted) return;
        /*
         * ⚠️ Any row with `accountLead === undefined` means the server predates
         * the key, so the answer is unknown, never "leads nothing".
         */
        const known = envelope.data.every((row) => row.accountLead !== undefined);
        setLedState({
          scopedTo: userId,
          regions: known
            ? envelope.data
                .filter((row) => row.accountLead?.id === userId)
                .map((row) => ({ slug: row.slug, name: row.name }))
            : null,
        });
      })
      .catch(() => {
        if (signal.aborted) return;
        setLedState({ scopedTo: userId, regions: null });
      });

    return () => controller.abort();
  }, [userId]);

  const ledRegions =
    ledState !== null && ledState.scopedTo === userId ? ledState.regions : null;

  /*
   * ⚠️ A remembered `region` choice for somebody KNOWN to lead no region reads as
   * `mine` — the option is not offered, so its figures may not be shown under
   * another label. Unknown (`null`) keeps the request: the server answers it.
   */
  const subject: TargetSubjectScope =
    requestedSubject === 'region' && ledRegions !== null && ledRegions.length === 0
      ? 'mine'
      : requestedSubject;

  /*
   * The period list is read ONCE per user, not once per picker change: moving
   * from Q1 to Q2 cannot change which cycle covers today, so re-listing would
   * spend a request per click to read back an answer that cannot have moved.
   * ⚠️ *SUPERSEDED: "against a 120-per-60s per-IP budget shared across the whole
   * office."* It is **12,000/60s** (corrected 2026-09-17); the reason is that
   * the list cannot have changed, not that the budget is narrow.
   */
  useEffect(() => {
    if (userId === null) return;
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listTargetPeriods({ isActive: true, perPage: 100 }, signal)
      .then((page) => {
        if (signal.aborted) return;
        setPeriodState({
          scopedTo: userId,
          period: periodForToday(page.data),
          periods: page.data,
          error: null,
        });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setPeriodState({
          scopedTo: userId,
          period: null,
          periods: [],
          error: caught instanceof Error ? caught.message : 'Could not read your figures.',
        });
      });

    return () => controller.abort();
  }, [userId]);

  const scoped = periodState !== null && periodState.scopedTo === userId ? periodState : null;
  const period = scoped?.period ?? null;
  /*
   * ⚠️ **Read off the same stamped slice as `period`**, so a list belonging to the
   * person just left can never be offered under the new one's figures.
   */
  const periods: readonly TargetPeriodRecord[] = scoped?.periods ?? [];

  /*
   * Resolved during render so the effect below can depend on the window's
   * PRIMITIVES. Depending on the object itself would refetch on every render:
   * `periodWindow` builds a fresh one each time and nothing memoises it.
   */
  const asked: TargetWindow | null =
    choice.kind === 'none'
      ? null
      : choice.kind === 'window'
        ? choice.window
        : period === null
          ? null
          : periodWindow(period);
  const askedKey = windowKey(asked);
  /*
   * 🔴 **THE IDENTITY OF AN ANSWER, AND THE CURRENCY IS PART OF IT.**
   *
   * ⚠️ **`askedKey` alone is the identity of a QUESTION'S WINDOW and is NOT the
   * identity of its ANSWER.** Two requests differing only in currency share a
   * window, so keyed on `askedKey` the cached figures would survive a currency
   * change and be relabelled with the new code — GBP amounts reading USD, which
   * is the one outcome worse than the GBP-only understatement this parameter
   * exists to fix. Stamped with both, figures for the currency just left read as
   * ABSENT and the tiles fall back to their loading state, which is the same
   * protection the window stamp already buys.
   *
   * ⚠️ **`askedKey` keeps its own job** — the `=== ''` emptiness test below is
   * "has a window been asked for at all", which a currency must not answer.
   *
   * 🔴 **THE BILLING REGION IS THE THIRD SEGMENT, AND IT IS THE ONE WHOSE
   * OMISSION WOULD BE HARDEST TO SEE.** A region change leaves both the window
   * and the currency untouched, so without this segment the cached figures
   * survive it entirely: the whole population's revenue, correctly formatted,
   * correctly dated, in the right currency, under a heading naming one band.
   * Every instrument reads green and the only wrong thing on screen is the
   * scope, which is the one part nothing else restates.
   *
   * ⚠️ **`undefined` and `'none'` give DIFFERENT keys** — `''` against `none` —
   * which they must, because "the whole population" and "the companies nobody
   * has banded" are different questions with different answers.
   */
  const figuresKey = `${askedKey}#${baseCurrency ?? ''}#${billingRegion ?? ''}#${subject}#${fxMode}`;
  const askedName = asked?.name ?? '';
  const askedFrom = asked?.from ?? '';
  const askedTo = asked?.to ?? '';
  const askedPeriodId = asked?.periodId ?? null;

  useEffect(() => {
    if (userId === null || askedKey === '') return;
    const controller = new AbortController();
    const { signal } = controller;

    /*
     * Rebuilt from the primitives rather than closed over, so every value this
     * effect reads is also a value it depends on. A window carrying an id is
     * asked for BY id: sending its dates instead is a different question that
     * happens to share an answer today and stops sharing one the moment a
     * period's boundary is corrected.
     */
    const askedWindow: TargetWindow = {
      name: askedName,
      from: askedFrom,
      to: askedTo,
      periodId: askedPeriodId,
    };
    /*
     * 🔴 **THE CURRENCY IS SENT NOW — 2026-09-13, owner's decision.**
     *
     * ⚠️ *SUPERSEDED, and every clause of it was false by the time it was read:
     * "**NO CURRENCY IS SENT, AND THAT IS UNCHANGED BY THE GBP-ONLY RULING.**
     * Owner's 2026-09-02 decision makes the dashboards GBP-only, but the
     * exclusion is taken SERVER-SIDE against its own base-currency constant —
     * `/targets/actuals` accepts no currency parameter, so sending
     * `DASHBOARD_CURRENCY` here would be a key the strict schema refuses by
     * name. The ruling is a DISPLAY decision (`targets.ts`), not a request one;
     * if a currency parameter is ever added, `DASHBOARD_CURRENCY` is what goes
     * in it."*
     *
     * The endpoint takes one — `TargetActualsCurrency` in `types.ts` declares
     * `baseCurrency`, and `use-team-performance.ts` has been sending it since
     * 2026-09-11 — and the GBP-only ruling it reasoned from was withdrawn by the
     * owner the same day. So the comment survived its own subject twice over
     * while the page it serves silently reported one invoice in fourteen:
     * measured on production, 651 USD invoices against 89 EUR and 56 GBP.
     *
     * ⚠️ **A CLAIM ABOUT ANOTHER REPO'S SCHEMA IS THE SHAPE THAT ROTS WITHOUT
     * ANNOUNCING ITSELF.** Nothing here goes red when a strict schema gains a
     * key — the comment stays perfectly readable and stops being true — and this
     * one said "a key the strict schema refuses BY NAME", which is precise,
     * confident and exactly the kind of sentence nobody re-measures. **Read the
     * type, not the note about the type.**
     *
     * ⚠️ **Spread CONDITIONALLY rather than sent as `undefined`.** Every query
     * schema on this API is `.strict()` and `listQuery` serialises what it is
     * given — an explicit `undefined` is a key the server may refuse, where an
     * absent one is the documented default of GBP. Same handling, and the same
     * reason, as `useTargetActuals`.
     */
    const currencyQuery: TargetActualsCurrency =
      baseCurrency === undefined ? {} : { baseCurrency };
    /*
     * ⚠️ **Spread CONDITIONALLY, and ANNOTATED, which is the half that matters.**
     * A conditional spread is exempt from excess-property checking, so this key
     * would compile, serialise and reach a `.strict()` schema even if
     * `TargetActualsParams` declared nothing called `billingRegion` — a green
     * build over a request the server refuses by name. This repo has shipped
     * exactly that on `/revenue-tracker`, on this very key. The annotation is
     * what makes the declaration load-bearing rather than decorative: it is
     * checked against `TargetActualsRegion`, so a key that is not on the
     * interface fails HERE.
     *
     * The value is `undefined`-versus-absent for the usual reason: `listQuery`
     * serialises what it is given, and an explicit `undefined` is a key the
     * server may refuse where an absent one is the documented whole population.
     */
    // `scope=my_region` beside a `billingRegion` is a 422 (targets.schema.ts), so
    // the region read never sends one.
    const regionQuery: TargetActualsRegion =
      billingRegion === undefined || billingRegion === null || subject === 'region'
        ? {}
        : { billingRegion };
    /*
     * `region` sends NO subject: the server resolves the caller's own billing
     * regions (the acted-as user in a support session) and refuses nobody.
     */
    const subjectQuery: TargetActualsSubject =
      subject === 'overall'
        ? { subjectType: 'organisation' }
        : subject === 'region'
          ? {}
          : { subjectType: 'user', subjectId: userId };
    const scopeQuery: TargetActualsScope = subject === 'region' ? { scope: 'my_region' } : {};
    // Absent = document mode (owner 2026-10-28); only `period_end` is sent.
    const fxQuery: { fxMode?: TargetFxMode } = fxMode === 'document' ? {} : { fxMode };
    const query: TargetActualsParams =
      askedPeriodId === null
        ? {
            from: askedFrom,
            to: askedTo,
            ...subjectQuery,
            ...scopeQuery,
            ...currencyQuery,
            ...regionQuery,
            ...fxQuery,
          }
        : {
            periodId: askedPeriodId,
            ...subjectQuery,
            ...scopeQuery,
            ...currencyQuery,
            ...regionQuery,
            ...fxQuery,
          };

    api
      .targetActuals(query, signal)
      .then((answer) => {
        if (signal.aborted) return;
        /*
         * ⚠️ **THE WHOLE ROW IS STORED, DELIBERATELY — DO NOT NARROW THIS TO
         * THE FIELDS A TILE HAPPENS TO READ TODAY.** `excludedByCurrency` was on
         * the wire for weeks and reached no screen, because it was in no type and
         * therefore in nobody's loader, and nothing anywhere went red about it.
         * Keeping the row whole means a field the server adds is carried by
         * construction and only the type has to admit it exists; picking fields
         * here would put a second place between the response and the screen where
         * one can be dropped in silence.
         */
        const byMetric: Partial<Record<TargetMetric, TargetActual>> = {};
        for (const row of answer.data.metrics) byMetric[row.metric] = row;
        setFigures({
          key: figuresKey,
          byMetric,
          window: measuredWindow(askedWindow, answer.data),
          /*
           * ⚠️ **THE SERVER'S ANSWER, AND ABSENT IS NOT THE REQUEST ECHOED
           * BACK.** A server predating the parameter omits the field and summed
           * in GBP; recording `baseCurrency` here instead would turn "we asked"
           * into "it was honoured", which is the proxy-field shape and would
           * label a GBP figure with whatever the reader picked.
           */
          measuredInCurrency: answer.data.measuredInCurrency ?? null,
          /*
           * ⚠️ **THE SERVER'S ANSWER, AND ABSENT IS NOT THE REQUEST ECHOED
           * BACK** — the same rule as the line above, and the same reason.
           * Recording `billingRegion` here would turn "we asked" into "it was
           * honoured": an API predating the parameter narrows nothing and says
           * nothing, and this field would then name a band the figure was never
           * restricted to.
           */
          measuredForBillingRegion: answer.data.billingRegion ?? null,
          // The server's echo; absent = an older API = document mode.
          fxMode: answer.data.fxMode ?? 'document',
          translation: answer.data.translation ?? null,
          actualsQuery: query,
          // The server's echo only; absent stays absent (an older API).
          regions: subject === 'region' ? answer.data.regions : undefined,
          error: null,
        });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setFigures({
          key: figuresKey,
          byMetric: {},
          window: askedWindow,
          // Nothing was measured, so nothing may claim a currency — or a scope.
          measuredInCurrency: null,
          measuredForBillingRegion: null,
          fxMode: 'document',
          translation: null,
          actualsQuery: null,
          regions: undefined,
          error: caught instanceof Error ? caught.message : 'Could not read your figures.',
        });
      });

    return () => controller.abort();
  }, [
    userId,
    figuresKey,
    askedKey,
    askedName,
    askedFrom,
    askedTo,
    askedPeriodId,
    baseCurrency,
    billingRegion,
    subject,
    fxMode,
  ]);

  if (userId === null) return EMPTY;
  if (periodState === null || periodState.scopedTo !== userId)
    return { ...EMPTY, ledRegions, subject, loading: true };
  if (periodState.error !== null)
    return { ...EMPTY, ledRegions, subject, error: periodState.error };

  /*
   * Nothing was asked for, so nothing is loading and nothing failed. `period`
   * and `periods` still come back: the picker is built from them, and a caller
   * that has asked for no window is precisely the caller mid-way through
   * choosing one.
   *
   * 🔴 **IT NOW RUNS BEFORE THE `noPeriod` GUARD, AND THE SWAP IS DELIBERATE.**
   * *SUPERSEDED: `if (period === null) return { ...EMPTY, noPeriod: true };` came
   * first.* A half-typed DATE RANGE needs no catalogued period at all, so
   * answering "no period covers today" to somebody mid-way through typing one
   * named the wrong obstacle. Both callers compute `blocked` from the choice
   * themselves and pass it to `tileState` ahead of `noPeriod`, so nothing on
   * screen moves — the hook now agrees with what they already did.
   */
  if (choice.kind === 'none') return { ...EMPTY, period, periods, ledRegions, subject };

  /*
   * 🔴 **`asked === null` IS THE `noPeriod` TEST NOW, NOT `period === null` —
   * 2026-09-13, and the old test would have DEADLOCKED THE NEW CYCLE CONTROL.**
   *
   * *SUPERSEDED: `if (period === null) return { ...EMPTY, noPeriod: true };`*,
   * which asked *"does a catalogued period cover today"*. A reader may now CHOOSE
   * a cycle that does not — the migration cataloguing one period per financial
   * cycle makes a set adrift of the calendar perfectly ordinary — and that choice
   * arrives as `{kind: 'window'}` carrying a real window. The old guard would have
   * answered `noPeriod` over it, blanked the tiles, **and returned `EMPTY`, which
   * carries no `periods`** — so the cycle list would have emptied and there would
   * have been no way back out of the state. A panel whose only exit is the control
   * it has just taken away.
   *
   * ⚠️ **After the `'none'` branch above, `asked === null` is EXACTLY "the choice
   * is `period` and nothing covers today"** — read `asked`'s own derivation: a
   * `'window'` choice always carries one. So this is the same question, asked of
   * the thing that answers it.
   *
   * ⚠️ **`periods` is carried THROUGH the refusal**, for the same reason the
   * branch above carries it: this is the one state a reader escapes by choosing a
   * cycle, so the list must survive the sentence that tells them to.
   */
  if (asked === null) return { ...EMPTY, periods, ledRegions, subject, noPeriod: true };

  /*
   * ⚠️ Matched on the KEY, so figures for the window — **or the currency** —
   * just left are treated as absent rather than shown under the new one's label.
   * That is the whole point of stamping them: the tiles fall back to their
   * loading state, where a carried-over figure would sit silently under the
   * wrong dates or the wrong unit.
   */
  const current = figures !== null && figures.key === figuresKey ? figures : null;
  if (current === null)
    return { ...EMPTY, period, periods, ledRegions, subject, window: asked, loading: true };

  return {
    byMetric: current.byMetric,
    period,
    periods,
    window: current.window,
    measuredInCurrency: current.measuredInCurrency,
    measuredForBillingRegion: current.measuredForBillingRegion,
    fxMode: current.fxMode,
    translation: current.translation,
    actualsQuery: current.actualsQuery,
    regions: current.regions,
    ledRegions,
    subject,
    loading: false,
    error: current.error,
    noPeriod: false,
  };
}
